import React, { useRef, useEffect } from 'react';
import { Box, Typography, Button, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

// Cloudflare FourColumnCards vector
const GREEN_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/FourColumnCardsVector.svg';

// Cloudflare Arrow icon
const ARROW_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/Tailless-Line-Arrow-Right-Circle.svg';

const ScrollingCardsMobileComponent = ({ data }) => {
  const { title, description, footer, footerLink, cards } = data;
  const shouldShowScrollbar = (cards || []).length > 0;
  const hasFooter = Boolean(String(footer || '').trim());
  const navigate = useNavigate();
  const scrollRef = useRef();
  const trackRef = useRef();
  const thumbRef = useRef();

  // refs for each card button
  const buttonRefs = useRef([]);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const initialScrollLeft = useRef(0);

  const CARD_PX = 295; // fixed card width

  // bounce animation (resolves when done)
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85, // your chosen bounce-back
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleButtonClick = async (index, link) => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      await gs_bounce(buttonRefs.current[index]);
    }

    navigate(link);

    // ✅ mobile-friendly: scroll after navigation
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReduced ? 'auto' : 'smooth',
      });
    });
  };

  const updateThumb = () => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = el;
    const trackWidth = track.clientWidth;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

    if (!scrollWidth || !clientWidth) return;

    if (maxScrollLeft === 0) {
      thumb.style.width = `${trackWidth}px`;
      thumb.style.left = '0px';
      return;
    }

    const ratio = clientWidth / scrollWidth;
    const width = Math.max(24, Math.min(trackWidth, ratio * trackWidth));
    const left = (scrollLeft / maxScrollLeft) * (trackWidth - width);

    thumb.style.width = `${width}px`;
    thumb.style.left = `${left}px`;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateThumb();
          ticking = false;
        });
        ticking = true;
      }
    };

    updateThumb();
    el.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateThumb);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumb);
    };
  }, []);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    initialScrollLeft.current = scrollRef.current.scrollLeft;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;

    const deltaX = e.clientX - dragStartX.current;
    const scrollableWidth = el.scrollWidth - el.clientWidth;
    const trackWidth = track.clientWidth - thumb.clientWidth;
    const scrollDelta = (deltaX / trackWidth) * scrollableWidth;
    el.scrollLeft = initialScrollLeft.current + scrollDelta;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  return (
    <ScrollFadeSection sx={{ width: '100%', maxWidth: '100vw' }}>
      <ScrollFadeWrapper variant="text">
        <Typography
          component={'h3'}
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            color: '#162F3B',
            textAlign: 'center',
            letterSpacing: '-0.03em',
            mb: '16px',
            whiteSpace: 'pre-line',
          }}
        >
          {title}
        </Typography>
      </ScrollFadeWrapper>

      <ScrollFadeWrapper variant="text" delay={0.2}>
        <Typography
          component={'p'}
          sx={{
            fontSize: '1.125rem',
            lineHeight: '1.5rem',
            fontWeight: 400,
            textAlign: 'center',
            mb: '40px',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            px: '16px',
            alignSelf: 'stretch',
            boxSizing: 'border-box',
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Typography>
      </ScrollFadeWrapper>

      {/* Full-bleed horizontal scroller + center snapping */}
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x pan-y',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },

          width: '100vw',
          mx: 'calc(50% - 50vw)',
          gap: '16px',
          pb: shouldShowScrollbar || hasFooter ? '56px' : '0px',
          alignItems: 'stretch',

          // snap to center of each card
          scrollSnapType: 'x mandatory',
          // ensure first/last card can center correctly
          scrollPaddingInline: `calc((100vw - ${CARD_PX}px)/2)`,
          overscrollBehaviorX: 'contain',
        }}
      >
        {/* Leading spacer (no snap) */}
        <Box
          sx={{
            flex: `0 0 clamp(8px, calc((100vw - ${CARD_PX}px)/2), 48px)`,
          }}
        />

        {cards.map((card, i) => (
          <ScrollFadeWrapper
            key={i}
            delay={0.4 + i * 0.1}
            sx={{
              width: `${CARD_PX}px`,
              minWidth: `${CARD_PX}px`,
              maxWidth: `${CARD_PX}px`,
              flexShrink: 0,
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '500px',
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '32px',
                boxShadow:
                  '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{ position: 'relative', width: '102px', height: '102px' }}
                >
                  <Image
                    src={GREEN_VECTOR_URL}
                    alt="Comparison cards graphic"
                    width={102}
                    height={102}
                    sx={{ width: '100%', height: '100%' }}
                  />
                  <Image
                    src={card.icon}
                    alt={card.imgAltTag || card.title}
                    title={card.imageAltTitle || card.title}
                    width={48}
                    height={48}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '48px',
                      height: '48px',
                    }}
                  />
                </Box>

                <Typography
                  component={'h3'}
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.03em',
                    color: '#162F3B',
                    textAlign: 'center',
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  component={'p'}
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    letterSpacing: '-0.002em',
                    fontWeight: 400,
                    color: '#162F3B',
                    textAlign: 'left',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 9,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {card.body}
                </Typography>
              </Box>

              {card.buttonText !== null && (
                <Button
                  ref={(el) => (buttonRefs.current[i] = el)}
                  variant="contained"
                  disableElevation
                  onClick={() => handleButtonClick(i, card.link)}
                  endIcon={
                    <Image
                      src={ARROW_ICON_URL}
                      alt="Arrow icon"
                      width={24}
                      height={24}
                      sx={{ width: 24, height: 24 }}
                    />
                  }
                  sx={{
                    width: '100%',
                    maxWidth: '231px',
                    height: '48px',
                    gap: '4px',
                    borderRadius: '24px',
                    backgroundColor: '#E3E6E8',
                    textTransform: 'none',
                    mt: 'auto',
                    willChange: 'transform',
                    '&:hover': { backgroundColor: '#b7bc00' },
                  }}
                >
                  <Typography
                    component={'a'}
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      color: '#162F3B',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    {card.buttonText || 'Compare quotes'}
                  </Typography>
                </Button>
              )}

              {card.buttonText === null && card.readMore && card.link && (
                <Box
                  sx={{
                    mt: 'auto',
                    pt: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <Link
                    href={card.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      color: '#A2ACB1',
                      textAlign: 'center',
                      '&:hover': {
                        color: '#9EA900',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Read more
                  </Link>
                </Box>
              )}
            </Box>
          </ScrollFadeWrapper>
        ))}

        {/* Trailing spacer (no snap) */}
        <Box
          sx={{
            flex: `0 0 clamp(8px, calc((100vw - ${CARD_PX}px)/2), 48px)`,
          }}
        />
      </Box>

      {shouldShowScrollbar && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '0px' }}>
          <Box
            ref={trackRef}
            sx={{
              height: '8px',
              width: '90%',
              borderRadius: '24px',
              backgroundColor: '#E0E0E0',
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <Box
              ref={thumbRef}
              onMouseDown={handleMouseDown}
              sx={{
                position: 'absolute',
                height: '8px',
                backgroundColor: '#C7CA00',
                borderRadius: '1000px',
                transition: isDragging.current ? 'none' : 'left 0.1s',
              }}
            />
          </Box>
        </Box>
      )}

      {hasFooter && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '32px' }}>
          <LinkWithIconComponent
            href={footerLink || undefined}
            iconSize="0.875rem"
            sx={{
              textAlign: 'center',
              fontSize: '1rem',
              fontWeight: 700,
              color: '#162F3B',
              maxWidth: '596px',
            }}
          >
            {footer}
          </LinkWithIconComponent>
        </Box>
      )}
    </ScrollFadeSection>
  );
};

export default ScrollingCardsMobileComponent;
